// @flow
import type { MapfileAnimationType } from 'catena/er2_map/src/map'
import type { UserLayerType } from 'catena/er2_map_userlayers/js/reducers/mapsources'
import * as actionTypes from '../constants/action_types'
import * as ids from '../constants/ids'

export type ExceptionType = {
    status: number,
    statusText: string,
}

export type FicusThemeType = {
    classification: [],
    mapfiles: MapfileAnimationType[],
    meta: {
        grayswan: [],
    },
}

export type TransimThemeType = {
    ficusNodePathText: string,
    meta: {
        classification: [],
        order: [],
        title: string,
    },
    transimAttrs: string[],
    transimNames: string[],
    transims: UserLayerType[],
}

export type FicusDataType = {
    frameworkInfo: string,
    frameworkInfoUrl: string,
    intervals: string[],
    themes: FicusThemeType[] | TransimThemeType,
    themeType: 'raster' | 'vector',
}

export type FrameworkType = {
    children: [],
    collapsed: boolean,
    data: {
        description_path?: string,
        has_rasters: boolean,
        has_temporal: boolean,
        has_vectors: boolean,
        is_dir: boolean,
        is_empty: boolean,
        modes: string[],
        path: string,
    },
    text: string,
}

export type SelectFrameworkBarType = {
    exception?: ExceptionType,
    // If false, then zoom to the extent of the theme
    extentHandled: boolean,
    fetchingFicusData: boolean,
    fetchingFrameworks: boolean,
    fetchingFrameworkInfo: boolean,
    frameworks: FrameworkType[],
    ficusNode: string,
    ficusData?: FicusDataType,
    ficusDataCache: {
        string?: FicusDataType,
    },
    hidden: boolean,
    visible: boolean,
}

type SelectFrameworkBarActionType = {
    type: string,
    id: string,
    active?: boolean,
    exception?: ExceptionType,
    fetching?: boolean,
    fetchingFrameworkInfo?: boolean,
    frameworks?: FrameworkType[],
    // Current ficusData
    ficusData: FicusDataType,
    ficusNode?: string,
    frameworkInfo?: string,
    frameworkInfoUrl?: string,
    mode?: string,
}

function addToFrameworks(frameworks, nodePath, nodeFrameworks) {
    if (nodePath) {
        frameworks.forEach((framework) => {
            if (framework.data.path === nodePath) {
                framework.children = nodeFrameworks
            } else if (framework.children.length) {
                addToFrameworks(framework.children, nodePath, nodeFrameworks)
            }
        })
        return frameworks
    }
    // Nothing to modify, so assume this is the initial set of frameworks
    return nodeFrameworks
}

const nullThemeData = {
    frameworkInfo: '<h3>No framework documentation is available</h3>',
    frameworkInfoUrl: '',
    themes: [],
    mode: '',
    realizationsDir: '',
}

const initialState = {
    exception: null,
    extentHandled: false,
    fetchingFicusData: false,
    fetchingFrameworks: false,
    fetchingFrameworkInfo: false,
    ficusDataCache: {},
    frameworks: null,
    ficusData: nullThemeData,
    ficusNode: '',
    hidden: false,
    visible: true,
}

export function findPath(ficusNode: string, data: FrameworkType[]) {
    const sel = []
    function loop(children: any) {
        for (let i = 0; i < children.length; i += 1) {
            const item = children[i]
            if (ficusNode === item.data.path) {
                sel.push(item)
                return
            }
            if (item.children) {
                loop(item.children)
                if (sel.length) {
                    sel.push(item)
                    return
                }
            }
        }
    }
    loop(data)
    return sel.map(i => i.text).reverse().join(' > ')
}

export default function selectFrameworkBar(state: SelectFrameworkBarType = initialState, action: SelectFrameworkBarActionType) {
    let key

    switch (action.type) {
    case actionTypes.CLICK_CONTEXT_BAR_ITEM:
        if (action.id === ids.SELECT_FRAMEWORK) {
            // Toggle the control bar if clicking an already-active framework button.
            // Otherwise, always show the control bar.
            return (action.active
                ? { ...state, visible: !state.visible }
                : { ...state, visible: true })
        }
        return state

    case actionTypes.CLEAR_FRAMEWORK:
        return { ...state, ficusData: nullThemeData }

    case actionTypes.CLOSE_CONTEXT_BAR_ITEMS:
        return ({ ...state, hidden: state.visible, visible: false })

    case actionTypes.EXTENT_HANDLED:
        return ({ ...state, extentHandled: true })

    case actionTypes.OPEN_CONTEXT_BAR_ITEMS:
        return { ...state, visible: state.hidden, hidden: false }

    case actionTypes.SET_FETCHING_FRAMEWORKS:
        return {
            ...state,
            exception: action.exception,
            fetchingFrameworks: action.fetching,
            // If there is an exception, then set frameworks to empty to indicate that loading is finished.
            frameworks: action.exception ? [] : state.frameworks,
        }

    case actionTypes.SET_FETCHING_FICUS_DATA:
        return {
            ...state,
            fetchingFicusData: action.fetching,
        }

    case actionTypes.RECEIVE_FRAMEWORKS:
        return {
            ...state,
            frameworks: addToFrameworks(state.frameworks, action.nodePath, action.frameworks),
        }

    case actionTypes.RECEIVE_FICUS_DATA:
        key = `${action.ficusNode || 'default'}_${action.mode || ''}`
        return {
            ...state,
            ficusData: { ...action.ficusData },
            // Add to ficusData cache
            ficusDataCache: { ...state.ficusDataCache, [key]: action.ficusData },
            ficusNode: action.ficusNode,
        }

    case actionTypes.SET_FETCHING_FRAMEWORK_INFO:
        return {
            ...state,
            fetchingFrameworkInfo: action.fetching,
            frameworksInfo: '',
            frameworksInfoUrl: '',
        }

    case actionTypes.RECEIVE_FICUS_FRAMEWORK_INFO:
        return {
            ...state,
            ficusData: {
                ...state.ficusData,
                frameworkInfo: action.frameworkInfo,
                frameworkInfoUrl: action.frameworkInfoUrl,
            },
        }

    case actionTypes.SET_FICUS_FRAMEWORK_INFO_URL:
        return {
            ...state,
            ficusData: {
                ...state.ficusData,
                frameworkInfoUrl: action.frameworkInfoUrl,
            },
        }

    default:
        return state
    }
}
